import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspaceParams } from '../hooks/useWorkspaceParams';
import { useWallet } from '../hooks/useWallet';
import { useInstanceStore } from '../stores/UseInstanceStore';
import { portfolioFetch } from '../utils/portfolioFetch';

// Workspace layout components
import { WsTitle } from '../components/workspace/WsTitle';
import { WsTabs } from '../components/workspace/WsTabs';
import { WorkspaceSidebar } from '../components/workspace/WorkspaceSidebar';
import { MobileTabBar } from '../components/workspace/MobileTabBar';
import { PersonaDrawer } from '../components/workspace/PersonaDrawer';

// Tab content components
import PortfolioPerformance from '../components/workspace/PortfolioPerformance';
import { WorkspaceSettings } from '../components/workspace/WorkspaceSettings';
import PortfolioRunResearchTab from '../components/research/PortfolioRunResearchTab';
import TradingRecommendations from '../components/TradingRecommendations';
import TradesList from '../components/TradesList';
import { HLAgentDebugPanel } from '../components/HLAgentDebugPanel';
import AgentKeyModal from '../components/AgentKeyModal';
import { OnboardingWizard } from '../components/onboarding/OnboardingWizard';

/**
 * WorkspacePage — unified portfolio + agent view.
 * Replaces both PortfolioDetailPage and AgentDetailPage.
 *
 * Route: /workspace/:portfolioId
 * Tabs driven by ?tab= URL param via useWorkspaceParams.
 */
export function WorkspacePage({ config }) {
  const { portfolioId } = useParams();
  const { tab, setTab, modal } = useWorkspaceParams();
  const { jwtToken, isTestnetNetwork } = useWallet();
  const navigate = useNavigate();

  const onRequestAgentOnboard = (portfolio) => {
    navigate('/dashboard', { state: { onboardPortfolioId: portfolio?.id, onboardPortfolioName: portfolio?.name } });
  };
  const { instance, bumpAgentKeyVersion } = useInstanceStore();
  const agentKeyVersion = instance.agentKeyVersion || 0;

  // Portfolio state
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasDataRef = useRef(false);
  const [error, setError] = useState(null);
  const [lastFetchedPortfolioId, setLastFetchedPortfolioId] = useState(null);

  // Time window for performance chart
  const [timeWindow, setTimeWindow] = useState(() => {
    const saved = localStorage.getItem(`portfolio_${portfolioId}_timeWindow`);
    return saved || '24H';
  });

  // Polling
  const pollInterval = 30000;
  const [nextRefreshIn, setNextRefreshIn] = useState(0);

  // Agent assignment state
  const [availableAgents, setAvailableAgents] = useState([]);
  const [assigningAgent, setAssigningAgent] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Agent key modal
  const [showAgentKeyModal, setShowAgentKeyModal] = useState(false);

  // Clear stale data on portfolioId change
  useEffect(() => {
    if (portfolioId !== lastFetchedPortfolioId) {
      hasDataRef.current = false;
      setPortfolio(null);
      setError(null);
      setLoading(true);
      setLastFetchedPortfolioId(portfolioId);
    }
  }, [portfolioId, lastFetchedPortfolioId]);

  // Persist time window
  useEffect(() => {
    localStorage.setItem(`portfolio_${portfolioId}_timeWindow`, timeWindow);
  }, [portfolioId, timeWindow]);

  // Fetch portfolio data
  const fetchPortfolio = useCallback(async () => {
    if (!portfolioId || portfolioId === 'null' || portfolioId === 'undefined') {
      setPortfolio(null);
      setLoading(false);
      return;
    }
    if (!jwtToken) {
      setLoading(false);
      return;
    }

    try {
      if (!hasDataRef.current) setLoading(true);

      const url = new URL(`${config.apiBaseUrl}/portfolio/${portfolioId}`);

      let granularity;
      switch (timeWindow) {
        case '24H': granularity = '15m'; break;
        case '7D': granularity = '4h'; break;
        case '30D': granularity = '24h'; break;
        default: granularity = '1h';
      }
      url.searchParams.append('granularity', granularity);
      url.searchParams.append('limit', '100');
      url.searchParams.append('window', timeWindow);

      const headers = {
        'accept': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      };

      const response = await portfolioFetch(url.toString(), { headers }, portfolioId);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) errorMessage = errorData.detail;
        } catch (_) { /* ignore parse error */ }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setPortfolio(data);
      hasDataRef.current = true;
      setError(null);
      if (pollInterval > 0) setNextRefreshIn(pollInterval / 1000);
    } catch (err) {
      console.error('Failed to fetch portfolio:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, config.apiBaseUrl, jwtToken, timeWindow, agentKeyVersion]);

  // Fetch available agents for assignment
  useEffect(() => {
    if (!jwtToken) return;
    fetch(`${config.apiBaseUrl}/agents`, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(setAvailableAgents)
      .catch(() => setAvailableAgents([]));
  }, [config.apiBaseUrl, jwtToken]);

  // Initial fetch + polling
  useEffect(() => {
    fetchPortfolio();
    if (pollInterval <= 0) return;
    const interval = setInterval(fetchPortfolio, pollInterval);
    return () => clearInterval(interval);
  }, [fetchPortfolio, pollInterval]);

  // Countdown timer
  useEffect(() => {
    if (pollInterval <= 0) return;
    const timer = setInterval(() => {
      setNextRefreshIn(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [pollInterval]);

  // Handle agent assignment
  const handleAssignAgent = useCallback(async (agentId) => {
    if (!agentId) return;
    setAssigningAgent(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}/portfolio/${portfolioId}/agent`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ agent_id: parseInt(agentId, 10), role: 'primary' }),
      });
      if (!res.ok) throw new Error(`Assignment failed (${res.status})`);
      fetchPortfolio();
    } catch (err) {
      console.error('Agent assignment failed:', err);
    } finally {
      setAssigningAgent(false);
    }
  }, [portfolioId, config.apiBaseUrl, jwtToken, fetchPortfolio]);

  // Handle position closed
  const handlePositionClosed = useCallback((positionId) => {
    setPortfolio(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        open_positions: prev.open_positions.filter(p => p.position_id !== positionId),
      };
    });
    fetchPortfolio();
  }, [fetchPortfolio]);

  // Tab fallback: if tab requires agent but none assigned, show performance
  const hasAgent = !!portfolio?.active_agent;
  const effectiveTab = (!hasAgent && (tab === 'research' || tab === 'recommendations')) ? 'performance' : tab;

  const totalPnl = parseFloat(portfolio?.realized_pnl_usd || 0);
  const totalPnlPct = parseFloat(portfolio?.realized_pnl_pct || 0);
  const canUseAgentFeatures = hasAgent && portfolio?.observer_role !== 'observer';

  // Loading state
  if (loading && !portfolio) {
    return (
      <div className="flex min-h-screen bg-slate-950">
        <WorkspaceSidebar className="hidden md:flex" portfolioId={portfolioId} />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-12 w-12 text-slate-400 mb-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <span className="text-slate-400">Loading workspace...</span>
          </div>
        </div>
        <MobileTabBar className="md:hidden" />
      </div>
    );
  }

  // Error state
  if (error && !portfolio) {
    const isAgentKeyError = error.includes('X-Agent-Private-Key header is required');
    return (
      <div className="flex min-h-screen bg-slate-950">
        <WorkspaceSidebar className="hidden md:flex" portfolioId={portfolioId} />
        <div className="flex-1 p-4">
          <div className="bg-red-900/20 border border-red-800 rounded-lg p-6">
            <h3 className="text-red-400 font-semibold mb-1">Failed to load workspace</h3>
            <p className="text-red-300 text-sm">{error}</p>
            <div className="flex gap-2 mt-3">
              {isAgentKeyError && (
                <button
                  onClick={() => setShowAgentKeyModal(true)}
                  className="px-4 py-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded text-sm"
                >
                  Set Agent Key
                </button>
              )}
              <button
                onClick={fetchPortfolio}
                className="px-4 py-2 bg-red-800 hover:bg-red-700 text-white rounded text-sm"
              >
                Retry
              </button>
            </div>
          </div>
          <AgentKeyModal
            isOpen={showAgentKeyModal}
            portfolioId={portfolioId}
            isTestnet={isTestnetNetwork()}
            onSave={() => { setShowAgentKeyModal(false); bumpAgentKeyVersion(); fetchPortfolio(); }}
            onClose={() => setShowAgentKeyModal(false)}
          />
        </div>
        <MobileTabBar className="md:hidden" />
      </div>
    );
  }

  if (!portfolio) return null;

  return (
    <div className="flex min-h-screen bg-slate-950">
      {/* Desktop sidebar */}
      <WorkspaceSidebar className="hidden md:flex" portfolioId={portfolioId} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Title bar */}
        <WsTitle
          portfolio={portfolio}
          agent={portfolio.active_agent}
          onEditPersona={() => setDrawerOpen(true)}
        />

        {/* Builder-fee banner */}
        {hasAgent && portfolio.portfolio_type === 'HYPERLIQUID' && !portfolio.builder_fee_approved && (
          <div className="mx-2 sm:mx-4 mb-2 p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <span className="text-yellow-300 text-sm font-medium">Builder fee approval required</span>
              <span className="text-yellow-300/60 text-xs block">Go to the Setup tab to approve.</span>
            </div>
            <button
              onClick={() => setTab('setup')}
              className="px-3 py-1 bg-yellow-700 hover:bg-yellow-600 text-white rounded text-xs font-medium"
            >
              Approve
            </button>
          </div>
        )}

        {/* No-agent banner */}
        {!hasAgent && (
          <div className="mx-2 sm:mx-4 mb-2 p-4 bg-amber-900/30 border border-amber-700/50 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h4 className="text-amber-400 font-semibold mb-1">No agent assigned</h4>
                <p className="text-amber-300/80 text-sm mb-3">
                  This portfolio cannot run research or execute trades until a primary agent is assigned.
                </p>
                <div className="flex flex-wrap gap-2 items-center">
                  {onRequestAgentOnboard && (
                    <>
                      <button
                        onClick={() => onRequestAgentOnboard({ id: parseInt(portfolioId, 10), name: portfolio?.name })}
                        className="px-3 py-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded text-sm font-semibold transition-colors"
                      >
                        + Create & Assign New Agent
                      </button>
                      {availableAgents.length > 0 && (
                        <span className="text-amber-400/50 text-xs">or assign existing:</span>
                      )}
                    </>
                  )}
                  {availableAgents.length > 0 && (
                    <select
                      disabled={assigningAgent}
                      onChange={(e) => {
                        if (e.target.value) handleAssignAgent(e.target.value);
                      }}
                      className="flex-1 max-w-xs px-3 py-2 bg-amber-950 border border-amber-700 rounded text-amber-100 text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Select an agent...</option>
                      {availableAgents.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  )}
                  {assigningAgent && <span className="text-amber-300 text-xs">Assigning...</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab strip */}
        <WsTabs
          tab={effectiveTab}
          setTab={setTab}
          hasAgent={hasAgent}
          timeWindow={timeWindow}
          onTimeWindowChange={(w) => {
            localStorage.setItem(`portfolio_${portfolioId}_timeWindow`, w);
            setTimeWindow(w);
          }}
          onRefresh={fetchPortfolio}
          loading={loading}
          pollInterval={pollInterval}
          nextRefreshIn={nextRefreshIn}
        />

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-1 sm:p-2 md:p-4 pb-20 md:pb-4">
          {effectiveTab === 'performance' && (
            <PortfolioPerformance
              portfolio={portfolio}
              totalPnl={totalPnl}
              totalPnlPct={totalPnlPct}
              portfolioId={portfolioId}
              config={config}
              handlePositionClosed={handlePositionClosed}
              timeWindow={timeWindow}
            />
          )}
          {effectiveTab === 'research' && canUseAgentFeatures && (
            <PortfolioRunResearchTab
              portfolioId={portfolioId}
              portfolio={portfolio}
              config={config}
              authToken={jwtToken}
              onTokensChange={fetchPortfolio}
            />
          )}
          {effectiveTab === 'recommendations' && canUseAgentFeatures && (
            <TradingRecommendations
              portfolioId={portfolioId}
              config={config}
              portfolio={portfolio}
              onTradeExecuted={fetchPortfolio}
            />
          )}
          {effectiveTab === 'trades' && (
            <TradesList portfolioId={portfolioId} config={config} />
          )}
          {effectiveTab === 'setup' && (
            <WorkspaceSettings
              portfolio={portfolio}
              agent={portfolio.active_agent}
              config={config}
              onAgentChange={fetchPortfolio}
              onTokensChange={fetchPortfolio}
            />
          )}
        </div>
      </div>

      {/* Persona drawer overlay */}
      {drawerOpen && portfolio.active_agent && (
        <PersonaDrawer
          agent={portfolio.active_agent}
          portfolio={portfolio}
          config={config}
          onSave={fetchPortfolio}
          onClose={() => setDrawerOpen(false)}
        />
      )}

      {/* Onboarding wizard modal */}
      {modal === 'onboarding' && (
        <OnboardingWizard
          portfolioId={portfolioId}
          portfolioName={portfolio?.portfolio_name}
          config={config}
          onComplete={fetchPortfolio}
        />
      )}

      {/* Mobile bottom tab bar */}
      <MobileTabBar className="md:hidden" />
    </div>
  );
}
